import { http } from '../request'
import type { PaginationData } from '../request'
import type {
    AccountListItem,
    AccountDetail,
    AccountSearchRequest
} from '../types/accounts'
import type { GameRank } from '../types/ranks'
import type { GameSkin } from '../types/skins'
import type { GunSkin } from '../types/gunSkins'
import type { OperatorSkin } from '../types/operatorSkins'
import type { HomeBanner } from '../types/support'

export type MarketDisplay = 'desktop' | 'mobile'
export type MarketBanner = Pick<
    HomeBanner,
    'id' | 'title' | 'kicker' | 'description' | 'image_url' | 'link_url' | 'display' | 'sort_order'
>

export interface MarketBootstrap {
    zone_counts: {
        all: number
        large: number
        medium: number
        small: number
        special: number
        split: number
    }
    game_version_counts?: {
        delta?: number
        delta_steam?: number
    }
    banners: MarketBanner[]
    ranks: GameRank[]
}

export interface MarketFilterOptions {
    knife_skins: GameSkin[]
    gun_skins: GunSkin[]
    operator_skins: OperatorSkin[]
}

/**
 * 账号市场相关 API - 公开访问，无需登录
 */
export const marketApi = {
    /**
     * 搜索市场账号列表
     * GET /api/market/accounts/
     */
    searchAccounts(params?: AccountSearchRequest): Promise<PaginationData<AccountListItem>> {
        return http.list<AccountListItem>('/api/market/accounts/', params)
    },

    /**
     * 获取市场账号详情
     * GET /api/market/accounts/{id}/
     */
    getAccountDetail(id: number): Promise<AccountDetail> {
        return http.get<AccountDetail>(`/api/market/accounts/${id}/`)
    },

    getBootstrap(display: MarketDisplay): Promise<MarketBootstrap> {
        return http.get<MarketBootstrap>('/api/market/accounts/bootstrap/', { params: { display } })
    },

    getFilterOptions(): Promise<MarketFilterOptions> {
        return http.get<MarketFilterOptions>('/api/market/accounts/filter-options/')
    }
}

const bootstrapRequests = new Map<MarketDisplay, Promise<MarketBootstrap>>()
let filterOptionsRequest: Promise<MarketFilterOptions> | null = null

export const loadMarketBootstrap = (display: MarketDisplay): Promise<MarketBootstrap> => {
    const existing = bootstrapRequests.get(display)
    if (existing) return existing

    const request = marketApi.getBootstrap(display).catch(error => {
        bootstrapRequests.delete(display)
        throw error
    })
    bootstrapRequests.set(display, request)
    return request
}

export const loadMarketFilterOptions = (): Promise<MarketFilterOptions> => {
    if (filterOptionsRequest) return filterOptionsRequest

    filterOptionsRequest = marketApi.getFilterOptions().catch(error => {
        filterOptionsRequest = null
        throw error
    })
    return filterOptionsRequest
}

export default marketApi
